import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Clock, Loader2, TriangleAlert } from 'lucide-react';
import { getDonationStatus, formatOre, type DonationStatus } from '../lib/donations';

const MAX_ATTEMPTS = 10;
const POLL_MS = 1500;

type View =
  | { kind: 'loading' }
  | { kind: 'thanks'; amountOre: number; awaiting: boolean }
  | { kind: 'failed' }
  | { kind: 'pending' } // confirmed nothing yet after the poll budget
  | { kind: 'error' };

/**
 * Spec 007 — the return page after Stripe checkout. The confirmation arrives by webhook (async), so this
 * polls the donation status by its session id and NEVER shows a thank-you before it is actually paid.
 * States: loading (confirming) → thanks (paid, or awaiting_posting — still received) → failed/cancelled
 * (specific "didn’t complete") → pending after the budget (neutral "we’re confirming; a receipt follows").
 * A missing session id is a specific error, not a blank page.
 */
export default function DonateThanks() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [view, setView] = useState<View>({ kind: 'loading' });
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setView({ kind: 'error' });
      return;
    }
    let active = true;
    let timer: ReturnType<typeof setTimeout>;

    const terminalFor = (status: DonationStatus, amountOre: number): View | null => {
      if (status === 'paid' || status === 'awaiting_posting' || status === 'refunded') {
        return { kind: 'thanks', amountOre, awaiting: status === 'awaiting_posting' };
      }
      if (status === 'failed' || status === 'cancelled') return { kind: 'failed' };
      return null; // still pending
    };

    const poll = async () => {
      attempts.current += 1;
      const r = await getDonationStatus(sessionId);
      if (!active) return;
      if (r.kind === 'notfound') {
        setView({ kind: 'error' });
        return;
      }
      if (r.kind === 'ok') {
        const settled = terminalFor(r.data.status, r.data.amountOre);
        if (settled) {
          setView(settled);
          return;
        }
      }
      // Still pending (or a transient fetch error) — retry until the budget is spent.
      if (attempts.current >= MAX_ATTEMPTS) {
        setView({ kind: 'pending' });
        return;
      }
      timer = setTimeout(poll, POLL_MS);
    };

    void poll();
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [sessionId]);

  return (
    <div className="mx-auto w-full max-w-lg px-6 py-24 text-center" aria-live="polite">
      {view.kind === 'loading' && (
        <>
          <Loader2 aria-hidden className="mx-auto h-9 w-9 animate-spin text-accent" />
          <h1 className="mt-4 font-display text-2xl font-semibold uppercase tracking-[0.12em] text-ink">
            Confirming your gift
          </h1>
          <p className="mt-3 font-serif text-lg leading-relaxed text-muted">
            One moment while we confirm the payment with Stripe…
          </p>
        </>
      )}

      {view.kind === 'thanks' && (
        <>
          <CheckCircle2 aria-hidden className="mx-auto h-10 w-10 text-accent" />
          <h1 className="mt-4 font-display text-2xl font-semibold uppercase tracking-[0.12em] text-ink">
            Thank you
          </h1>
          <p className="mt-3 font-serif text-lg leading-relaxed text-muted">
            Your gift of <strong className="text-ink">{formatOre(view.amountOre)}</strong> supports the
            order on a cost-recovery basis (§7). It appears in our open books for every member to see.
            {view.awaiting ? ' It will be booked as soon as the current financial year opens.' : ''}
          </p>
          <Link
            to="/"
            className="mt-8 inline-flex items-center justify-center rounded-md border border-border bg-surface px-4 py-2 font-sans text-sm text-ink transition-colors hover:border-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            Back to the site
          </Link>
        </>
      )}

      {view.kind === 'failed' && (
        <>
          <TriangleAlert aria-hidden className="mx-auto h-10 w-10 text-error" />
          <h1 className="mt-4 font-display text-2xl font-semibold uppercase tracking-[0.12em] text-ink">
            Payment didn’t complete
          </h1>
          <p className="mt-3 font-serif text-lg leading-relaxed text-muted">
            The payment was cancelled or declined, so nothing was charged. You’re welcome to try again.
          </p>
          <Link
            to="/donate"
            className="mt-8 inline-flex items-center justify-center rounded-md bg-accent px-4 py-2 font-sans text-sm font-medium text-bg transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            Try again
          </Link>
        </>
      )}

      {view.kind === 'pending' && (
        <>
          <Clock aria-hidden className="mx-auto h-10 w-10 text-muted" />
          <h1 className="mt-4 font-display text-2xl font-semibold uppercase tracking-[0.12em] text-ink">
            We’re confirming your gift
          </h1>
          <p className="mt-3 font-serif text-lg leading-relaxed text-muted">
            This is taking a little longer than usual. If you left an email, a receipt will follow once the
            payment is confirmed. You don’t need to do anything.
          </p>
          <Link
            to="/"
            className="mt-8 inline-flex items-center justify-center rounded-md border border-border bg-surface px-4 py-2 font-sans text-sm text-ink transition-colors hover:border-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            Back to the site
          </Link>
        </>
      )}

      {view.kind === 'error' && (
        <>
          <TriangleAlert aria-hidden className="mx-auto h-10 w-10 text-error" />
          <h1 className="mt-4 font-display text-2xl font-semibold uppercase tracking-[0.12em] text-ink">
            We couldn’t find that donation
          </h1>
          <p className="mt-3 font-serif text-lg leading-relaxed text-muted">
            This link is missing its payment reference. If you just donated and left an email, your receipt
            will still arrive once the payment is confirmed.
          </p>
          <Link
            to="/donate"
            className="mt-8 inline-flex items-center justify-center rounded-md border border-border bg-surface px-4 py-2 font-sans text-sm text-ink transition-colors hover:border-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            Make a donation
          </Link>
        </>
      )}
    </div>
  );
}
